import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IoEye } from 'react-icons/io5';
import MatchDetailsDesktop from './MatchDetailsDesktop';
import { createMatchEntryViewModel, getCompactGameModeLabel } from './match/matchViewModels';
import {
    AssetImage,
    AugmentGrid,
    ChampionAvatar,
    DamageBars,
    formatHistoryRankLabel,
    getToneStyles,
    HighestStreakBadge,
    KdaValue,
    EloBadge,
    PerformanceRankBadge,
    RuneIcons,
    SquareAsset,
    SpellIcons,
    SummaryItemGrid,
    ToneBadge,
} from './match/MatchSharedElements';

const NEUTRAL_BADGE_CLASS_NAME = 'inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/65 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] leading-none text-zinc-500';
const CARD_CHROME_RIGHT_RAIL_CLASS_NAME = 'ml-auto flex w-[148px] shrink-0 items-center justify-end text-right';

function formatDesktopHistoryRankLabel(averageElo) {
    if (averageElo?.isApex) {
        const lpLabel = Number(averageElo.leaguePoints || 0);
        const badgeLabel = String(averageElo.badge || '');
        return `${badgeLabel}-${lpLabel}`.trim();
    }

    return formatHistoryRankLabel(averageElo);
}

function renderDesktopHistoryRankLabelContent(averageElo) {
    const label = formatDesktopHistoryRankLabel(averageElo);

    if (!averageElo?.badge || averageElo.isApex || averageElo.badge === 'UR') {
        return label;
    }

    const [tierLabel, divisionLabel] = label.split(' ');

    if (!tierLabel || !divisionLabel) {
        return label;
    }

    return (
        <>
            <span className="text-[0.98em] font-semibold tracking-[0.02em]">{tierLabel}</span>
            <span className="ml-1 font-mono text-[0.98em] font-black tracking-[-0.08em]">
                {divisionLabel}
            </span>
        </>
    );
}

function buildCombatLine(player, isArena) {
    return `${player.kills} / ${player.deaths} / ${player.assists}`;
}

function hasDenseCombatLine(player) {
    return [player.kills, player.deaths, player.assists].every((value) => String(value ?? '').length >= 2);
}

function MetaBadge({ children, className = '' }) {
    return <div className={`${NEUTRAL_BADGE_CLASS_NAME} ${className}`}>{children}</div>;
}

function InlineMetric({ className = '', icon, iconNode = null, label, value, valueNode = null }) {
    return (
        <div className={`flex h-6 items-center justify-between gap-2 rounded-full border border-zinc-800 bg-zinc-950/85 px-2 text-[10px] font-medium leading-none text-zinc-300 ${className}`}>
            <div className="flex min-w-0 items-center gap-1">
                {iconNode || (icon ? <img alt={`${label} icon`} className="h-3.5 w-3.5 shrink-0 filter brightness-0 invert" src={icon} /> : null)}
                <span className="truncate text-[9px] uppercase leading-none tracking-[0.12em] text-zinc-300">{label}</span>
            </div>
            {valueNode ? (
                <span className="inline-flex shrink-0 items-center text-right font-semibold leading-none text-zinc-100">{valueNode}</span>
            ) : (
                <span className="inline-flex shrink-0 items-center text-right font-semibold leading-none text-zinc-100">{value}</span>
            )}
        </div>
    );
}

function KdaMetricValue({ isArena, player }) {
    return (
        <span className="inline-flex shrink-0 items-center gap-1 text-right font-semibold leading-none text-zinc-100">
            <KdaValue className="leading-none tabular-nums" iconSize={13} isPerfectKda={player.isPerfectKda} value={player.kda} />
            {!isArena ? <span className="inline-flex items-center text-[10px] leading-none text-zinc-300">({player.killParticipation}%)</span> : null}
        </span>
    );
}

function ParticipantPreview({ displayRegion, onParticipantNavigate, participant }) {
    const toneStyles = getToneStyles(participant.tone);

    return (
        <Link
            className={`group mb-px flex h-[21px] w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-zinc-300 transition-colors hover:bg-zinc-950/78 hover:text-zinc-100 ${
                participant.isFocus ? toneStyles.previewHighlight : ''
            }`}
            onClick={onParticipantNavigate}
            to={`/summoners/${displayRegion}/${participant.linkSlug}`}
        >
            <div className="h-4 w-6 shrink-0 overflow-hidden rounded-sm border border-zinc-500/60 bg-zinc-900">
                <AssetImage alt="participant champion icon" className="h-full w-full scale-[1.18] object-cover" src={participant.championIconSrc} />
            </div>
            <span className={`min-w-0 truncate text-[10.5px] ${participant.isFocus ? 'font-semibold' : ''}`}>{participant.gameName}</span>
        </Link>
    );
}

function ArenaTeamParticipantPill({ displayRegion, onParticipantNavigate, participant, tone }) {
    const toneStyles = getToneStyles(tone);

    return (
        <Link
            className={`flex h-6 min-w-0 items-center gap-1 rounded-md border px-1.5 text-[10px] text-zinc-300 transition-colors hover:text-zinc-100 ${participant.isFocus ? `${toneStyles.focusPillSurface} ${toneStyles.previewHighlight}` : toneStyles.rowSurface}`}
            onClick={onParticipantNavigate}
            to={`/summoners/${displayRegion}/${participant.linkSlug}`}
        >
            <div className="h-4 w-4 shrink-0 overflow-hidden rounded-full border border-zinc-600/70 bg-zinc-900">
                <AssetImage alt="participant champion icon" className="h-full w-full scale-[1.18] object-cover" src={participant.championIconSrc} />
            </div>
            <span className={`min-w-0 truncate text-[10.5px] ${participant.isFocus ? 'font-semibold' : ''}`}>{participant.gameName}</span>
        </Link>
    );
}

function ArenaTopTeamsDesktopGrid({ displayRegion, onParticipantNavigate, teams }) {
    if (!teams?.length) {
        return null;
    }

    return (
        <div className="grid grid-cols-[44px_repeat(3,minmax(0,1fr))] gap-x-1 gap-y-1">
            {teams.map((team) => (
                <React.Fragment key={`${team.subteamId}-${team.placement}`}>
                    <div className="flex h-6 items-center justify-start">
                        <ToneBadge compact label={team.placementLabel} tone={team.tone} withDot={false} />
                    </div>
                    {team.participants.map((participant) => (
                        <ArenaTeamParticipantPill
                            displayRegion={displayRegion}
                            key={participant.puuid}
                            onParticipantNavigate={onParticipantNavigate}
                            participant={participant}
                            tone={team.tone}
                        />
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
}

function ArenaTriosDesktopAugmentGrid({ sources = [], tone }) {
    const toneStyles = getToneStyles(tone);

    return (
        <div className="grid grid-cols-3 gap-[2px]">
            {sources.slice(0, 6).map((src, index) => (
                <SquareAsset
                    alt="augment icon"
                    backgroundClassName={toneStyles.iconSurface}
                    className="rounded-md"
                    key={`${src}-${index}`}
                    size={19}
                    src={src}
                    tone={tone}
                />
            ))}
        </div>
    );
}

const MatchHistoryDesktop = ({ hideCardChrome = false, matchHistory, orderedChampions, setSummonerData, summonerData }) => {
    const { displayRegion } = useParams();
    const [expandedMatchIds, setExpandedMatchIds] = useState({});
    const matchEntries = (matchHistory || [])
        .map((matchRecord) => createMatchEntryViewModel(matchRecord, summonerData?.puuid))
        .filter(Boolean);

    const handleParticipantNavigate = () => {
        setSummonerData({});
    };

    const handleCardDetailsChange = (matchId, nextValue) => {
        setExpandedMatchIds((currentValue) => ({
            ...currentValue,
            [matchId]: nextValue,
        }));
    };

    const handleCardSurfaceClick = (matchId, event) => {
        if (!hideCardChrome) {
            return;
        }

        const interactiveTarget = event.target.closest('a, button, input, select, textarea, [role="button"]');

        if (interactiveTarget && interactiveTarget !== event.currentTarget) {
            return;
        }

        handleCardDetailsChange(matchId, !expandedMatchIds[matchId]);
    };

    if (!matchEntries.length) {
        const hasUnavailableArchives = (matchHistory || []).some((matchRecord) => matchRecord?.archive_unavailable);
        return (
            <div className="mx-auto w-full max-w-[980px] rounded-lg py-3 text-white">
                <div className="flex items-center justify-center">
                    <div className="rounded-md border border-amber-400/50 bg-amber-300/10 px-3 py-3 text-sm font-medium text-amber-100">
                        {hasUnavailableArchives ? 'Match history not available. Try updating.' : 'No games played. Try updating.'}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-[980px] rounded-lg text-white">
            {matchEntries.map((entry) => {
                const toneStyles = getToneStyles(entry.tone);
                const isExpanded = Boolean(expandedMatchIds[entry.id]);
                const compactGameModeLabel = getCompactGameModeLabel(entry.gameModeLabel);

                return (
                    <div
                        className={`relative mb-3 overflow-hidden rounded-xl border bg-zinc-950 shadow-[0_14px_34px_-26px_rgba(0,0,0,0.9)] transition-[transform,box-shadow,border-color,background-color] duration-200 ${
                            hideCardChrome
                                ? `cursor-pointer ${isExpanded ? 'border-zinc-600 bg-zinc-950 shadow-[0_18px_40px_-28px_rgba(0,0,0,0.92)]' : 'border-[#33333a] hover:-translate-y-[1px] hover:border-zinc-600 hover:bg-zinc-900 hover:shadow-[0_18px_40px_-28px_rgba(0,0,0,0.92)]'}`
                                : 'border-[#33333a]'
                        }`}
                        onClick={(event) => handleCardSurfaceClick(entry.id, event)}
                        onKeyDown={(event) => {
                            if (!hideCardChrome) {
                                return;
                            }

                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleCardDetailsChange(entry.id, !isExpanded);
                            }
                        }}
                        key={entry.id}
                        role={hideCardChrome ? 'button' : undefined}
                        tabIndex={hideCardChrome ? 0 : undefined}
                    >
                        <div className={`pointer-events-none absolute inset-y-0 left-0 w-[7px] rounded-l-lg ${toneStyles.accentRail}`} />
                        <div className={`pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-l-lg ${toneStyles.accentStripe}`} />

                        {!hideCardChrome ? (
                            <div className="border-b border-zinc-800/70 px-3 py-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                        <ToneBadge className="h-[22px]" label={entry.statusLabel} tone={entry.tone} />
                                        <PerformanceRankBadge rank={entry.player.performanceRank} />
                                        {entry.player.roleLabel ? (
                                            <MetaBadge className="h-[22px] gap-1.5">
                                                <img alt={`${entry.player.roleLabel} role icon`} className="h-3.5 w-3.5 opacity-85" src={entry.player.roleIconSrc} />
                                                <span>{entry.player.roleLabel}</span>
                                            </MetaBadge>
                                        ) : null}
                                        {entry.player.placementLabel ? (
                                            <ToneBadge compact label={entry.player.placementLabel} tone={entry.tone} withDot={false} />
                                        ) : null}
                                        {entry.player.highestStreak ? <HighestStreakBadge highestStreak={entry.player.highestStreak} /> : null}
                                    </div>

                                    <div className={`${CARD_CHROME_RIGHT_RAIL_CLASS_NAME} flex-nowrap gap-2 text-xxs italic ${toneStyles.accentText}`}>
                                        <span className="shrink-0 whitespace-nowrap opacity-90">{entry.timeAgoLabel}</span>
                                        <span className="shrink-0 whitespace-nowrap opacity-70">{entry.durationLabel}</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className={`grid min-h-[106px] items-stretch gap-x-2 px-3 pt-[7px] pb-[11px] ${entry.isTriosArena ? 'grid-cols-[72px_202px_minmax(0,1fr)]' : 'grid-cols-[72px_359px_minmax(0,1fr)]'}`}>
                            {entry.isTriosArena ? (
                                <div className="flex h-[106px] w-[72px] flex-col items-center pt-0.5">
                                    <ChampionAvatar
                                        championIconSrc={entry.player.championIconSrc}
                                        championLevel={entry.player.championLevel}
                                        imageSize={48}
                                        levelBadgeClassName="translate-x-[1px]"
                                        levelBadgeSize={17}
                                        tone={entry.player.tone}
                                    />
                                    <div className="mt-[5px] flex justify-center overflow-visible">
                                        <ArenaTriosDesktopAugmentGrid
                                            sources={entry.player.augmentSources}
                                            tone={entry.player.tone}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid h-[106px] justify-items-center gap-y-0.5 py-0.5 grid-rows-4">
                                    <div className="row-span-2 flex h-full w-12 items-center justify-center">
                                        <ChampionAvatar
                                            championIconSrc={entry.player.championIconSrc}
                                            championLevel={entry.player.championLevel}
                                            imageSize={48}
                                            levelBadgeClassName="translate-x-[1px]"
                                            levelBadgeSize={17}
                                            tone={entry.player.tone}
                                        />
                                    </div>
                                    <div className="flex h-6 w-12 items-center">
                                        <SpellIcons className="grid h-[22px] w-12 grid-cols-2 gap-1 overflow-hidden" size={22} sources={entry.player.spellSources} tone={entry.player.tone} />
                                    </div>
                                    <div className="flex h-6 w-12 items-center">
                                        {entry.isArena ? (
                                            <AugmentGrid className="justify-items-center" columns={3} size={17} sources={entry.player.augmentSources} tone={entry.player.tone} />
                                        ) : (
                                            <RuneIcons
                                                className="grid h-[22px] w-12 grid-cols-2 gap-1 overflow-hidden"
                                                primaryClassName="scale-[1.04] object-cover"
                                                primarySize={22}
                                                secondaryClassName="scale-[0.92] object-contain"
                                                secondaryInsetClassName=""
                                                secondarySize={22}
                                                sources={entry.player.runeSources}
                                                tone={entry.player.tone}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="min-w-0">
                                <div className="flex h-[106px] min-w-0 items-start gap-3">
                                    <div className="grid h-[106px] w-[194px] min-w-0 shrink-0 grid-rows-4 gap-y-0.5 py-0.5">
                                        <div className="grid h-6 w-[194px] min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-1.5 overflow-visible">
                                            <span className="inline-flex min-w-0 items-center overflow-hidden px-[2px] text-ellipsis whitespace-nowrap text-[11px] font-bold italic leading-none text-zinc-300">
                                                {compactGameModeLabel}
                                            </span>
                                            <span className={`relative -left-2.5 inline-flex min-w-0 items-center justify-end truncate font-semibold leading-none text-zinc-100 ${hasDenseCombatLine(entry.player) ? 'text-[10px]' : 'text-[10.5px]'}`}>{buildCombatLine(entry.player, entry.isArena)}</span>
                                            <div className="flex items-center justify-end">
                                                <EloBadge
                                                    badge={entry.averageEloBadge}
                                                    className="shrink-0 self-center text-[10px]"
                                                    contentClassName="text-[10px]"
                                                    iconClassName="-translate-x-0.5 mr-0.5 h-[20px] w-[20px]"
                                                    label={renderDesktopHistoryRankLabelContent(entry.averageEloDetails)}
                                                    showIcon
                                                />
                                            </div>
                                        </div>
                                        <div className="flex h-6 w-[194px] items-center">
                                            <SummaryItemGrid
                                                className="w-full justify-between gap-0 pt-0.5"
                                                itemSources={entry.player.itemSources}
                                                roleBoundItemSource={entry.player.roleBoundItemSource}
                                                size={19}
                                                tone={entry.player.tone}
                                                trinketSource={entry.player.trinketSource}
                                            />
                                        </div>
                                        <div className="row-span-2 w-[194px]">
                                            <DamageBars
                                                className="grid h-full grid-rows-2 gap-y-0.5"
                                                compact
                                                dealtLabel={entry.player.damageDealtLabel}
                                                dealtPercent={entry.player.damageDealtPercent}
                                                rowClassName="h-6 items-center"
                                                takenLabel={entry.player.damageTakenLabel}
                                                takenPercent={entry.player.damageTakenPercent}
                                                tone={entry.player.tone}
                                            />
                                        </div>
                                    </div>
                                    {!entry.isTriosArena ? (
                                <div className="grid h-[106px] w-[145px] shrink-0 grid-rows-4 gap-y-0.5 py-0.5">
                                            <InlineMetric
                                                className="w-full"
                                                icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-kda-icon.svg`}
                                                label="KDA"
                                                valueNode={<KdaMetricValue isArena={entry.isArena} player={entry.player} />}
                                            />
                                            {!entry.isArena ? (
                                                <InlineMetric
                                                    className="w-full"
                                                    icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/minions-slain.svg`}
                                                    label="CS"
                                                    value={`${entry.player.totalCs} · ${entry.player.csPerMinute.toFixed(1)}`}
                                                />
                                            ) : null}
                                            <InlineMetric
                                                className="w-full"
                                                icon={`${process.env.PUBLIC_URL}/assets/scoreboard_icons/scoreboard-coins-icon.svg`}
                                                label="Gold"
                                                value={`${entry.player.goldLabel} · ${Math.round(entry.player.goldPerMinute)}`}
                                            />
                                            <InlineMetric
                                                className="w-full"
                                                iconNode={<span className="inline-flex items-center"><IoEye className="h-3.5 w-3.5 shrink-0" /></span>}
                                                label="Vision"
                                                value={entry.player.visionScore}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            <div className={`flex h-[106px] min-w-0 flex-col py-0.5 ${entry.isTriosArena ? 'justify-start' : 'justify-between'}`}>
                                {entry.isTriosArena ? (
                                    <div className="space-y-1 pl-1">
                                        <ArenaTopTeamsDesktopGrid
                                            displayRegion={displayRegion}
                                            onParticipantNavigate={handleParticipantNavigate}
                                            teams={entry.topArenaTeams}
                                        />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-x-1 gap-y-0 pb-2">
                                        <div className="min-w-0">
                                            {entry.previewColumns[0]?.map((participant) => (
                                                <ParticipantPreview
                                                    displayRegion={displayRegion}
                                                    key={participant.puuid}
                                                    onParticipantNavigate={handleParticipantNavigate}
                                                    participant={participant}
                                                />
                                            ))}
                                        </div>
                                        <div className="min-w-0">
                                            {entry.previewColumns[1]?.map((participant) => (
                                                <ParticipantPreview
                                                    displayRegion={displayRegion}
                                                    key={participant.puuid}
                                                    onParticipantNavigate={handleParticipantNavigate}
                                                    participant={participant}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <MatchDetailsDesktop
                            displayRegion={displayRegion}
                            entry={entry}
                            hideFooter={hideCardChrome}
                            navAndSearchParticipant={handleParticipantNavigate}
                            onShowDetailsChange={(nextValue) => handleCardDetailsChange(entry.id, nextValue)}
                            orderedChampions={orderedChampions}
                            showDetails={hideCardChrome ? isExpanded : undefined}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default MatchHistoryDesktop;
